import { z } from "zod";

import {
  MEMBER_PROJECT_ACCESS,
  PROJECT_CREATION,
  type AuditPage,
  type AuditQuery,
  type OrganisationPolicies,
} from "../admin-types";
import { PROJECT_ROLES, type IdentityApi, type ProjectAccessRequest } from "../types";
import type { LiveCtx } from "./auth";
import { currentOrganisationId } from "./organisation";
import { ROUTES } from "./routes";
import { maybe, parseMembers, parser } from "./schemas";

type AdminApi = Pick<
  IdentityApi,
  | "changeMemberRole"
  | "suspendMember"
  | "reactivateMember"
  | "removeMember"
  | "getPolicies"
  | "updatePolicies"
  | "listAuditEvents"
  | "requestProjectAccess"
  | "myProjectAccessRequest"
  | "listProjectAccessRequests"
  | "decideProjectAccessRequest"
  | "selfAddToProject"
>;

const policiesSchema = z.object({
  memberProjectAccess: z.enum(MEMBER_PROJECT_ACCESS),
  projectCreation: z.enum(PROJECT_CREATION),
  sessionMaxHours: maybe(z.number().int()),
});

const party = z.object({ type: z.string(), id: maybe(z.string()), name: z.string() });

const auditPageSchema = z.object({
  events: z.array(
    z.object({
      id: z.string(),
      occurredAt: z.string(),
      actor: party,
      category: z.string(),
      action: z.string(),
      target: party,
      result: z.enum(["success", "refused"]),
      ipAddress: maybe(z.string()),
      requestId: z.string(),
      detail: z.record(z.string(), z.unknown()),
    }),
  ),
  next: maybe(z.string()),
});

const projectRequestSchema = z.object({
  id: z.string(),
  projectId: z.string(),
  projectName: z.string(),
  requester: z.object({
    id: z.string(),
    name: z.string(),
    email: z.string(),
    initials: z.string(),
    colour: z.number().int(),
  }),
  message: z.string(),
  status: z.enum(["pending", "approved", "declined", "expired"]),
  createdAt: z.string(),
  expiresAt: z.string(),
  role: maybe(z.enum(PROJECT_ROLES)),
});

const parsePolicies = parser<OrganisationPolicies>(policiesSchema);
const parseAuditPage = parser<AuditPage>(auditPageSchema);
const parseProjectRequest = parser<ProjectAccessRequest>(projectRequestSchema);
const parseProjectRequests = parser<ProjectAccessRequest[]>(z.array(projectRequestSchema));
const parseMaybeProjectRequest = parser<ProjectAccessRequest | null>(
  projectRequestSchema.nullable(),
);

/** The audit query as the service reads it; empty parts are left out. */
export function auditQueryString(query: AuditQuery): string {
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(query)) {
    if (value === undefined || value === "") continue;
    params.set(key, String(value));
  }
  return params.toString();
}

/**
 * The admin console over the identity service (27 §6, §8 `/orgs/:id/{members,policies,audit}`) and project access
 * requests (FR-ADMIN-028). Every right is checked again on the server.
 */
export function createAdminApi(ctx: LiveCtx): AdminApi {
  const { http } = ctx;
  const organisationId = () => currentOrganisationId(ctx);
  const memberAction = async (userId: string, action: "suspend" | "reactivate") => {
    const id = await organisationId();
    return http.send("POST", ROUTES.memberAction(id, userId, action), {}, parseMembers);
  };

  return {
    async changeMemberRole(userId, role) {
      const id = await organisationId();
      return http.send("PUT", ROUTES.memberAction(id, userId, "role"), { role }, parseMembers);
    },
    suspendMember: (userId) => memberAction(userId, "suspend"),
    reactivateMember: (userId) => memberAction(userId, "reactivate"),
    async removeMember(userId) {
      const id = await organisationId();
      return http.send("DELETE", ROUTES.member(id, userId), undefined, parseMembers);
    },

    async getPolicies() {
      return http.get(ROUTES.policies(await organisationId()), parsePolicies);
    },
    async updatePolicies(patch) {
      const id = await organisationId();
      return http.send("PATCH", ROUTES.policies(id), patch, parsePolicies);
    },
    async listAuditEvents(query) {
      const id = await organisationId();
      return http.get(ROUTES.audit(id, auditQueryString(query)), parseAuditPage);
    },

    async selfAddToProject(projectId, reason) {
      await http.sendOnly("POST", ROUTES.selfAddToProject(projectId), { reason });
    },
    async requestProjectAccess(projectId, message) {
      return http.send(
        "POST",
        ROUTES.projectAccessRequests(projectId),
        { message },
        parseProjectRequest,
      );
    },
    async myProjectAccessRequest(projectId) {
      return http.get(ROUTES.myProjectAccessRequest(projectId), parseMaybeProjectRequest);
    },
    async listProjectAccessRequests() {
      return http.get(ROUTES.reviewAccessRequests, parseProjectRequests);
    },
    async decideProjectAccessRequest(requestId, decision) {
      await http.sendOnly("POST", ROUTES.decideAccessRequest(requestId), decision);
    },
  };
}
